import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Store, CalendarCheck, Sparkles, MessageCircle, Mail, Phone } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import api, { formatApiError } from "@/lib/api";
import LocationSelect from "@/components/LocationSelect";
import PhoneInput from "@/components/PhoneInput";
import OtpModal, { otpApiError } from "@/components/OtpModal";

const blank = {
  email: "", password: "", password2: "", full_name: "",
  phoneDialCode: "+1", phoneNumber: "",
  country: "", state: "", city: "",
  interests: [],
};

const CommunityLanding = () => {
  const { t } = useI18n();
  const c = t.community;
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refSlug = (searchParams.get("ref") || "").toLowerCase().trim();
  const [form, setForm] = useState(blank);
  const [mode, setMode] = useState("email");
  const [otpOpen, setOtpOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const categoryKeys = useMemo(() => Object.keys(t.categories), [t]);
  const fullPhone = `${form.phoneDialCode}${form.phoneNumber}`;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleInterest = (k) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(k) ? f.interests.filter((x) => x !== k) : [...f.interests, k],
    }));
  };

  const doRegister = async () => {
    setError(""); setLoading(true);
    try {
      const payload = {
        password: form.password,
        full_name: form.full_name,
        phone: fullPhone,
        country: form.country, city: form.city, state: form.state,
        interests: form.interests, source: refSlug || "CommunityLanding",
      };
      if (mode === "email") payload.email = form.email;
      const { data } = await api.post("/auth/register-client", payload);
      if (data.access_token) localStorage.setItem("red.token", data.access_token);
      const loginId = mode === "email" ? form.email : fullPhone;
      try { await login(loginId, form.password); } catch (err) { console.error("auto-login failed:", err); }
      setDone(true);
    } catch (err) { setError(otpApiError(err, t) === t.otp.sendFailed ? formatApiError(err) : otpApiError(err, t)); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { setError(t.fields.passwordsDoNotMatch); return; }
    setError("");
    if (mode === "phone") { setOtpOpen(true); return; }
    await doRegister();
  };

  const benefits = [
    { Icon: Store, title: c.b1t, desc: c.b1d },
    { Icon: CalendarCheck, title: c.b2t, desc: c.b2d },
    { Icon: Sparkles, title: c.b3t, desc: c.b3d },
    { Icon: MessageCircle, title: c.b4t, desc: c.b4d },
  ];

  if (done) {
    return (
      <section className="min-h-[80vh] bg-cream flex items-center justify-center py-20">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-gray-200 p-10 text-center">
          <CheckCircle2 className="text-orange mx-auto" size={56} />
          <h1 className="font-display text-4xl text-teal-deep mt-4 leading-tight" data-testid="community-success">
            {c.successTitle}
          </h1>
          <p className="text-teal-soft mt-3">{c.successSub}</p>
          <button onClick={() => navigate("/directory")} className="btn-orange mt-8" data-testid="community-go-directory">
            {c.goDirectory} <ArrowRight size={14} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[90vh] bg-cream py-14 md:py-20">
      <div className="container-tight grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Left: pitch */}
        <div className="lg:sticky lg:top-28">
          <p className="eyebrow text-orange">{c.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-teal-deep leading-[1.05] mt-3" data-testid="community-title">
            {c.title}
          </h1>
          <p className="text-teal-soft mt-4 text-lg max-w-lg">{c.sub}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                <span className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <Icon size={19} />
                </span>
                <h3 className="font-bold text-teal-deep mt-3 text-sm">{title}</h3>
                <p className="text-xs text-teal-soft mt-1 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8 md:p-10">
          {refSlug && (
            <div className="mb-5 bg-orange/10 border border-orange/30 rounded-xl p-3 text-sm text-teal-deep inline-flex items-center gap-2" data-testid="community-ref-banner">
              <Sparkles size={14} className="text-orange shrink-0" />
              <span>Te registras a través de la campaña <b>{refSlug}</b>. ¡Gracias!</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-display text-3xl text-teal-deep leading-tight">{c.formTitle}</h2>
              <p className="text-teal-soft text-sm mt-1">{c.formSub}</p>
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider bg-teal/10 text-teal rounded-full px-3 py-1">{c.free}</span>
          </div>

          <form onSubmit={submit} className="mt-7 space-y-5" data-testid="community-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.otp.createWith}</label>
              <div className="grid grid-cols-2 gap-1 bg-cream rounded-xl p-1 mt-2" data-testid="cred-mode-toggle">
                <button
                  type="button" onClick={() => setMode("email")}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "email" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"}`}
                  data-testid="cred-mode-email"
                >
                  <Mail size={13} /> {t.otp.emailOpt}
                </button>
                <button
                  type="button" onClick={() => setMode("phone")}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "phone" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"}`}
                  data-testid="cred-mode-phone"
                >
                  <Phone size={13} /> {t.otp.phoneOpt}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.fields.fullName} <span className="text-red-500">*</span></label>
                <input required className="field-input mt-1" value={form.full_name} onChange={set("full_name")} data-testid="community-name" />
              </div>
              {mode === "email" && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.auth.email} <span className="text-red-500">*</span></label>
                  <input type="email" required className="field-input mt-1" value={form.email} onChange={set("email")} data-testid="community-email" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.fields.createPassword} <span className="text-red-500">*</span></label>
                <input type="password" required minLength={6} className="field-input mt-1" value={form.password} onChange={set("password")} data-testid="community-password" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.fields.confirmPassword} <span className="text-red-500">*</span></label>
                <input type="password" required minLength={6} className="field-input mt-1" value={form.password2} onChange={set("password2")} data-testid="community-password2" />
                {form.password2 && form.password !== form.password2 && (
                  <p className="text-xs text-red-600 mt-1">{t.fields.passwordsDoNotMatch}</p>
                )}
              </div>
            </div>

            <PhoneInput
              label={<>{t.fields.phone} <span className="text-red-500">*</span></>}
              value={{ dialCode: form.phoneDialCode, number: form.phoneNumber }}
              onChange={(v) => setForm({ ...form, phoneDialCode: v.dialCode, phoneNumber: v.number })}
            />

            <LocationSelect
              labels={{
                country: t.fields.country,
                state: t.fields.state,
                city: <>{t.fields.city} <span className="text-red-500">*</span></>,
              }}
              value={{ country: form.country, state: form.state, city: form.city }}
              onChange={(loc) => setForm({ ...form, ...loc })}
            />

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.auth.interests}</label>
              <p className="text-xs text-teal-soft mt-1">{t.auth.interestsHint}</p>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="community-interests">
                {categoryKeys.map((k) => (
                  <button
                    key={k} type="button" onClick={() => toggleInterest(k)}
                    className={`chip ${form.interests.includes(k) ? "active" : ""}`}
                    data-testid={`community-interest-${k}`}
                  >
                    {t.categories[k]}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-red-600" data-testid="community-error">{error}</p>}

            <button type="submit" disabled={loading} className="btn-orange w-full justify-center" data-testid="community-submit">
              {loading ? "…" : c.submit} <ArrowRight size={14} />
            </button>
          </form>

          <p className="text-sm text-teal-soft mt-6 text-center">
            {t.auth.hasAccount} <Link to="/login" className="text-orange font-bold hover:underline">{t.auth.loginLink}</Link>
          </p>
        </div>
      </div>

      <OtpModal
        open={otpOpen}
        phone={fullPhone}
        onClose={() => setOtpOpen(false)}
        onVerified={async () => { setOtpOpen(false); await doRegister(); }}
      />
    </section>
  );
};

export default CommunityLanding;
